import { CheckCircle2 } from "lucide-react";

/**
 * Phase UX-4.1 — Human-readable intake summary.
 *
 * Replaces the raw JSON dump on the admin matter overview with a clean,
 * lawyer-readable rendering of the canonical envelope.
 *
 * No backend changes — pure presentation of `matter.intake_data` and
 * `matter.entities`.
 */
export default function IntakeSummary({ intake = {}, entities = {} }) {
  const env = mergeEnvelope(intake, entities);
  const matterType = env.matter_type || env.service_type || "matter";

  return (
    <div className="space-y-6" data-testid="intake-summary">
      <ClientDetails env={env} matterType={matterType} />
      <MatterDetailsBlock env={env} matterType={matterType} />
      <RolePeopleBlock env={env} matterType={matterType} />
      <BeneficiariesBlock env={env} />
      <UnknownFieldsHint env={env} matterType={matterType} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section renderers
// ---------------------------------------------------------------------------

function ClientDetails({ env, matterType }) {
  const primaryRole = PRIMARY_ROLE_BY_TYPE[matterType] || "client";
  const primary = findPerson(env, primaryRole) || (env.persons || [])[0];
  if (!primary) return null;
  const rows = [
    ["Full name",       primary.full_name],
    ["ID number",       primary.id_number],
    ["Email",           primary.email],
    ["Phone",           primary.phone],
    ["Residential address", primary.residential_address],
    ["Marital status",  humanMaritalStatus(primary.marital_status)],
  ];
  return (
    <Section title={`${PRIMARY_ROLE_LABEL[primaryRole] || "Client"} details`}>
      <FieldRows rows={rows} />
    </Section>
  );
}

function RolePeopleBlock({ env, matterType }) {
  // Show extra people specific to the matter type (executor/guardian for wills,
  // founder/trustees for trusts, seller for conveyancing, etc.).
  if (matterType === "will") {
    return (
      <>
        <PersonRoleSection env={env} role="executor" title="Executor" />
        <PersonRoleSection env={env} role="guardian" title="Guardian" />
      </>
    );
  }
  if (matterType === "antenuptial") {
    return <SpousesSection env={env} />;
  }
  if (matterType === "conveyancing") {
    return <PersonRoleSection env={env} role="seller" title="Seller" />;
  }
  if (matterType === "trust") {
    return (
      <>
        <PersonRoleSection env={env} role="founder" title="Founder" />
        <PeopleRoleListSection env={env} role="trustee" title="Trustees" />
      </>
    );
  }
  if (matterType === "deceased_estate") {
    return (
      <>
        <PersonRoleSection env={env} role="deceased" title="The deceased" />
        <PersonRoleSection env={env} role="executor" title="Executor" />
      </>
    );
  }
  return null;
}

function PersonRoleSection({ env, role, title }) {
  const person = findPerson(env, role);
  if (!person) return null;
  if (!isRealName(person.full_name)) return null;
  return (
    <Section title={title} testid={`summary-${role}`}>
      <FieldRows rows={[
        ["Full name", person.full_name],
        ["ID number", person.id_number],
        ["Email",     person.email],
        ["Phone",     person.phone],
      ]} />
    </Section>
  );
}

function PeopleRoleListSection({ env, role, title }) {
  const people = (env.persons || []).filter((p) => p.role === role);
  if (!people.length) return null;
  return (
    <Section title={title} testid={`summary-${role}s`}>
      <ul className="space-y-2 text-sm" data-testid={`summary-${role}-list`}>
        {people.map((p, i) => (
          <li key={i} className="border-l-2 border-zinc-200 pl-3">
            <div className="font-medium text-zinc-900">{p.full_name || "—"}</div>
            <div className="text-xs text-zinc-500">
              {[p.id_number, p.email, p.phone].filter(Boolean).join(" · ") || "no contact details"}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function SpousesSection({ env }) {
  const spouses = (env.persons || []).filter((p) => p.role === "spouse");
  if (!spouses.length) return null;
  return (
    <Section title="Spouses" testid="summary-spouses">
      <div className="grid sm:grid-cols-2 gap-4">
        {spouses.map((p, i) => (
          <div key={i} className="border border-zinc-200 p-3" data-testid={`summary-spouse-${i}`}>
            <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-2">
              Spouse {i + 1}
            </div>
            <FieldRows compact rows={[
              ["Full name", p.full_name],
              ["ID number", p.id_number],
              ["Email", p.email],
              ["Address", p.residential_address],
            ]} />
          </div>
        ))}
      </div>
    </Section>
  );
}

function BeneficiariesBlock({ env }) {
  const beneficiaries = (env.persons || []).filter((p) => p.role === "beneficiary");
  if (!beneficiaries.length) return null;
  return (
    <Section title="Beneficiaries" testid="summary-beneficiaries">
      <ul className="divide-y divide-zinc-100 border border-zinc-200">
        {beneficiaries.map((p, i) => (
          <li key={i} className="px-4 py-3 flex items-center justify-between gap-3 text-sm"
              data-testid={`summary-beneficiary-${i}`}>
            <div className="min-w-0">
              <div className="font-medium text-zinc-900 truncate">{p.full_name || "—"}</div>
              <div className="text-xs text-zinc-500 truncate">
                {[p.relationship, p.id_number].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
            {p.share_pct !== undefined && p.share_pct !== null && (
              <span className="text-[11px] tracking-widest uppercase bg-zinc-100 text-zinc-700 px-2 py-0.5 shrink-0">
                {Number(p.share_pct).toFixed(0)}%
              </span>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function MatterDetailsBlock({ env, matterType }) {
  if (matterType === "conveyancing") {
    const prop = (env.properties || [])[0] || {};
    const tx = env.transaction || {};
    const rows = [
      ["Property address", prop.address],
      ["Property type",    prop.property_type && humanProperty(prop.property_type)],
      ["Purchase price",   tx.purchase_price && formatZAR(tx.purchase_price)],
      ["Bond required",    tx.has_bond === true ? "Yes" : tx.has_bond === false ? "No" : null],
      ["Bond amount",      tx.bond_amount && formatZAR(tx.bond_amount)],
      ["Bond bank",        tx.bond_bank],
      ["OTP signed",       tx.otp?.date_signed],
      ["Occupation date",  tx.otp?.occupation_date],
    ];
    if (!rows.some(([, v]) => isFilled(v))) return null;
    return <Section title="Key matter details"><FieldRows rows={rows} /></Section>;
  }
  if (matterType === "antenuptial") {
    const m = env.marriage || {};
    const rows = [
      ["Wedding date", m.wedding_date],
      ["Location",     m.location],
      ["Regime",       REGIME_LABEL[m.regime] || m.regime],
    ];
    if (!rows.some(([, v]) => isFilled(v))) return null;
    return <Section title="Key matter details"><FieldRows rows={rows} /></Section>;
  }
  if (matterType === "trust") {
    const t = env.trust || {};
    const rows = [
      ["Trust name",    t.trust_name],
      ["Trust type",    TRUST_TYPE_LABEL[t.trust_type] || t.trust_type],
      ["Purpose",       t.trust_purpose],
      ["Vesting",       t.vesting && t.vesting.charAt(0).toUpperCase() + t.vesting.slice(1)],
    ];
    if (!rows.some(([, v]) => isFilled(v))) return null;
    return <Section title="Trust details"><FieldRows rows={rows} /></Section>;
  }
  if (matterType === "deceased_estate") {
    const e = env.estate || {};
    const rows = [
      ["Date of death",            e.date_of_death],
      ["Estate value (estimate)",  e.estate_value_estimate && formatZAR(e.estate_value_estimate)],
      ["Will left?",               e.has_will === true ? "Yes" : e.has_will === false ? "No" : null],
      ["Reported to Master?",      e.reported_to_master === true ? "Yes" : e.reported_to_master === false ? "No" : null],
    ];
    if (!rows.some(([, v]) => isFilled(v))) return null;
    return <Section title="Estate details"><FieldRows rows={rows} /></Section>;
  }
  return null;
}

function UnknownFieldsHint({ env, matterType }) {
  if (!Object.keys(env).length) {
    return (
      <Section title="Intake summary">
        <p className="text-sm text-zinc-500">No intake data captured yet for this matter.</p>
      </Section>
    );
  }
  // Soft footnote — what's still missing per risk_engine, if available.
  const missing = (env.missing || []).slice(0, 6);
  if (!missing.length) return null;
  return (
    <Section title="Still required from client">
      <ul className="text-sm space-y-1">
        {missing.map((m, i) => (
          <li key={i} className="flex items-start gap-2 text-amber-700">
            <span className="text-amber-500">!</span> {m}
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Primitive UI helpers
// ---------------------------------------------------------------------------

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function FieldRows({ rows, compact = false }) {
  const visible = rows.filter(([, v]) => isFilled(v));
  if (!visible.length) {
    return <p className="text-sm text-zinc-400 italic">Not captured yet.</p>;
  }
  return (
    <dl className={compact ? "space-y-1.5 text-sm" : "grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm"}>
      {visible.map(([label, value], i) => (
        <div key={i} className={compact ? "flex justify-between gap-3" : "sm:contents"}>
          <dt className={compact ? "text-zinc-500 shrink-0" : "text-zinc-500 sm:col-span-1"}>
            {label}
          </dt>
          <dd className={compact ? "text-zinc-900 text-right truncate" : "text-zinc-900 sm:col-span-2"}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Plain JS helpers — no schema changes
// ---------------------------------------------------------------------------

function mergeEnvelope(intake, entities) {
  const out = { ...(intake || {}) };
  for (const key of ["persons", "marriage", "properties", "transaction",
                     "trust", "will", "estate"]) {
    if (entities?.[key] && (!out[key] || isEmpty(out[key]))) {
      out[key] = entities[key];
    }
  }
  return out;
}

function findPerson(env, role) {
  return (env.persons || []).find((p) => p.role === role) || null;
}

function isFilled(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v).length > 0;
  return true;
}

function isEmpty(v) { return !isFilled(v); }

function formatZAR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  return `R ${Number(n).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
}

function humanProperty(t) {
  return {
    freehold:    "Freehold (full title)",
    sectional:   "Sectional title",
    estate:      "Within an estate / HOA",
    vacant_land: "Vacant land",
  }[t] || t;
}

function humanMaritalStatus(t) {
  if (!t) return null;
  return {
    single:   "Single",
    married:  "Married",
    divorced: "Divorced",
    widowed:  "Widowed",
  }[t] || t;
}

const NON_NAME_TOKENS = new Set(["yes", "no", "n/a", "na", "none", "nil", "tbd", "tbc", "-", "?"]);

function isRealName(value) {
  if (typeof value !== "string") return false;
  const s = value.trim();
  if (s.length < 2) return false;
  if (NON_NAME_TOKENS.has(s.toLowerCase())) return false;
  return /[A-Za-z]/.test(s);
}

const PRIMARY_ROLE_BY_TYPE = {
  conveyancing:    "buyer",
  antenuptial:     "spouse",
  will:            "testator",
  trust:           "founder",
  deceased_estate: "executor",
};

const PRIMARY_ROLE_LABEL = {
  buyer:    "Buyer",
  spouse:   "Spouses",
  testator: "Testator",
  founder:  "Founder",
  executor: "Executor",
};

const REGIME_LABEL = {
  in_community:     "In community of property",
  out_with_accrual: "Out of community — with accrual",
  out_no_accrual:   "Out of community — without accrual",
  unknown:          "Not yet confirmed",
};

const TRUST_TYPE_LABEL = {
  inter_vivos:  "Inter-vivos (set up now)",
  testamentary: "Testamentary (set up on death)",
};

export { Section, FieldRows };
export const __testIcons = { CheckCircle2 };  // keeps the icon import used if needed
